/**
 * schoolCalendar — на кои КАЛЕНДАРНИ дати пада един седмичен слот, когато
 * категорията му има краен брой занятия.
 *
 * Защо съществува: календарите разгъваха всеки слот до 30 юни, без да знаят, че
 * `CategoryDefinition.weeksPerYear` е 20–36. Измерено на production 2026-09-04:
 * ~512 занятия, които няма да се проведат, между 7 и 13 седмици на учител.
 * Изчислителят е ЕДИН и двата календара минават през него — второ копие би се
 * разминало пак, както се разминаха двете копия на разгъването.
 *
 * Идиомът за дати е този на `payroll/calendar.ts`: работи се с ISO низове
 * ("YYYY-MM-DD") и `Date.UTC`, никога с локален `Date` от низ — иначе резултатът
 * зависи от часовата зона на машината. Повредени диапазони се игнорират, вместо
 * да чупят сметката.
 *
 * Чиста функция: без часовник (началната дата е параметър) и без i18n.
 */

import { BG_DAY_TO_DOW, EN_DAY_TO_DOW, type NonWorkingRange } from './payroll/calendar';

/**
 * Ден от седмицата за КАЛЕНДАРА — включва неделя.
 *
 * 🔴 Нарочно отделна от `dayNameToDow` (`payroll/calendar.ts`), която връща `undefined` за
 * неделя по конвенцията „неделя НИКОГА не е работен ден". Тази конвенция е вярна за
 * заплатите и НЕВЯРНА за календара: училище с неделни занятия съществува (решение на owner,
 * 2026-09-05 — днешното училище няма такива, но продуктът трябва да ги позволява), а дотогава
 * неделният слот получаваше `complete: false` и се разстилаше до 30 юни без нито един маркер,
 * тоест изглеждаше като счупени данни.
 *
 * Две конвенции, които досега бяха една. `dayNameToDow` и заплатите остават непокътнати —
 * промяна там би сменила очакваните часове в декларациите.
 *
 * ⚠️ Известна граница, приета съзнателно: `monthWorkingCalendar` (заплати) продължава да
 * брои неделя за нула, тоест за неделен слот календарът ще показва занятия, а декларацията
 * няма да очаква часове. Решение за заплатите, извън обхвата на тази задача.
 */
const CALENDAR_DAY_TO_DOW: Record<string, number> = {
  ...BG_DAY_TO_DOW,
  ...EN_DAY_TO_DOW,
  'неделя': 7,
  sunday: 7,
};

/** Ден от седмицата по име (bg или en), 1=понеделник … 7=неделя; `undefined` при непознат. */
export function calendarDayToDow(day: string): number | undefined {
  return CALENDAR_DAY_TO_DOW[String(day || '').trim().toLowerCase()];
}

export interface LessonDatesInput {
  /** Както е записан в слота ("saturday", "събота") — минава през `dayNameToDow`. */
  dayOfWeek: string;
  /** ISO "YYYY-MM-DD". Може да е в ДРУГ ден от седмицата — не се брои за занятие. */
  startDate: string;
  /** Брой занятия (`CategoryDefinition.weeksPerYear`). */
  lessonCount: number;
  /** Неучебните периоди — занятие в тях не се провежда и се пропуска. */
  nonWorking: NonWorkingRange[];
  /**
   * ОБЯВЕНИ учебни дни за ТОЗИ слот (C4b R0) — ISO дати, на които занятието се провежда
   * ВЪПРЕКИ ваканция.
   *
   * 🔴 Живее при СЛОТА, не при категорията (решение на owner-а, 07.09, отменя C4 R4). Причината
   * е радиусът: изключение при категорията докосва всеки клас, който я кара; при слота докосва
   * точно класа, на когото липсва занятието. Два съботни слота на една категория могат да имат
   * РАЗЛИЧНИ обявени дни.
   *
   * Дата, която не пада в деня от седмицата на слота, няма ефект — тя не създава занятие, а само
   * отменя пропускането. Занятие в друг ден е ЗАМЕСТВАЩО (R0б), различен механизъм.
   */
  declaredSchoolDays?: string[];
  /**
   * Последната дата, която календарът може да покаже (ISO). Занятие след нея не
   * се търси.
   *
   * Без този край изчислителят „избягва" от повреден период: диапазон, клипнат
   * към прозореца, е КРАЕН, тоест след него пак има свободни дати — и слот с 30
   * занятия получава пълен план, който започва цяла година по-късно. Мерено:
   * при период „2020-01-01 … 2077-12-31" и прозорец до 31 авг. 2027 планът
   * тръгваше от 2027-09-04.
   */
  endDate?: string;
}

export interface LessonDates {
  /** Подредени ISO дати, дължина ≤ `lessonCount`. */
  dates: string[];
  /** Последното занятие, или `null` при празен резултат — никога `undefined`. */
  lastDate: string | null;
  /**
   * Намерени са ВСИЧКИ поискани занятия (и са поне едно).
   *
   * Флагът, а не сравнение на дължини от страна на извикващия: правилото за
   * закръгляне и таванът живеят ТУК, и извикващият, който ги преповтаря, тихо
   * се разминава с тях при първата им промяна. `false` значи „този план не
   * важи" — календарът се връща към стария прозорец, вместо да обяви за
   * последно занятие ден, който не е.
   */
  complete: boolean;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Резултат, който НЕ важи — всички гардове минават оттук. */
const incomplete = (): LessonDates => ({ dates: [], lastDate: null, complete: false });

/** Затворен интервал от ISO дати — прозорецът, който календарът показва. */
/**
 * Началната дата на занятията за слот — ЕДИН резолвер за всички консуматори.
 *
 * Ред на предимство:
 *   1. изключението на категорията (`CategoryDefinition.startDate`);
 *   2. началото на учебната година от настройките (`settings/general.schoolYears[ключ]`);
 *   3. резерв: 1 септември на ПЪРВАТА календарна година от ключа.
 *
 * 🔴 Резервът се извежда от КЛЮЧА, не от часовника. `schoolYearBounds(today)` дава грешната
 * година, ако през август се планира следващата — точно случаят, който извади наяве F3 от
 * ревюто на задача B: админският календар вадеше годината от стенния часовник, а ваканциите
 * му идваха по ключа на активната година, тоест между смяната в настройките и 1 септември
 * двете сочеха различни години и всеки курс свършваше около две седмици по-рано.
 *
 * Невалиден или липсващ ключ → `null`; извикващият решава (календарите падат на старото
 * разгъване, а не изчезват).
 */
export function slotStartDate(input: {
  schoolYear: string | null | undefined;
  yearDates?: { schoolYearStartDate?: string } | null;
  categoryStartDate?: string | null;
}): string | null {
  const { schoolYear, yearDates, categoryStartDate } = input;

  // `isRealIsoDate`, не само формата: „2026-02-30" минава `ISO_DATE`, а `Date.UTC` го
  // превърта в март. Такъв запис е възможен — полето съществуваше преди сървърната
  // валидация и Firestore може да се редактира и пряко. Резолвер, който го приеме, връща
  // дата, която НЕ съществува, и планът тихо излиза непълен вместо да падне на резерва.
  if (categoryStartDate && isRealIsoDate(categoryStartDate)) return categoryStartDate;

  const fromSettings = yearDates?.schoolYearStartDate;
  if (fromSettings && isRealIsoDate(fromSettings)) return fromSettings;

  return schoolYearStartDate(schoolYear);
}

/**
 * Откога брои ТОЗИ слот, докъде му се смята планът, и важи ли той изобщо.
 *
 * 🔴 Нищо тук не зависи от ПРОЗОРЕЦА НА ЕКРАНА — само от слота и от КЛЮЧА на годината.
 *
 * Дотук `inWindow` се решаваше спрямо прозореца, а той се строи от `earliestSlotStart`:
 * на таблото минимум по ВСИЧКИ слотове на училището, при учителя — по неговите. Тоест
 * валидността на плана на един слот зависеше от чужди слотове. Измерено срещу `b83f182`:
 * чужд слот с начало 2026-06-15 (под прага 1 юли) свива прозореца на таблото обратно до
 * 1 септември, докато този на учителя остава разширен до 2026-08-20 — и същият слот получава
 * `inWindow: false` на таблото (резервен път, без маркер за край) и `inWindow: true` при
 * учителя (пълен план). Един слот, два различни плана, според чужди данни.
 *
 * Границите на годината идват от ключа (`absoluteWorkYearWindow`) и са еднакви навсякъде,
 * затова планът е един. Прозорецът на екрана остава само за две неща: клипване на
 * неучебните периоди и ограничаване на резервния път.
 */
export function resolveSlotPlan(input: {
  /** Решената дата на слота; липсва при още недеплойнат backend. */
  slotStartDate: string | null | undefined;
  /** Учебната година по ключ („2026-2027") — единственият друг вход. */
  schoolYear: string | null | undefined;
  /** Началото на годината, когато е известно — резерв за слот без своя дата. */
  yearStartDate: string;
  /**
   * 🔴 Докъде се търсят занятия — ОБХВАТЪТ, решен от извикващия (C4).
   *
   * Дотук това беше „краят на екранния прозорец", ползван само при нечетим ключ, а планът
   * иначе стигаше до 31 декември на втората година (`absoluteWorkYearWindow`). C4 отмени този
   * обхват: планът спира там, докъдето системата има ДАННИ за ваканции, тоест на края на
   * `holidayWindow` — надмножество на учебната година по построение.
   *
   * Решение на owner (2026-09-06): занятия отвъд него НЕ се рисуват и НЕ носят маркер.
   * Те никога няма да се проведат там — курс, който стига дотам, е НЕПОПРАВЕН курс и се
   * оправя с обявен учебен ден или втори ден от седмицата. Календарът не крие реални занятия;
   * той не рисува артефакти на непоправен курс. Сигналът е списъкът по R9 и стига САМО до
   * админа. ⚠️ Не „поправяй" това като тихо изрязване — избрано е, не е пропуснато.
   */
  scopeEndDate: string;
}): {
  /** Откога брои слотът. */
  startDate: string;
  /** Докъде се търсят занятия — краят на границите на годината, не на екранния прозорец. */
  endDate: string;
  /**
   * Датата е ИЗПОЛЗВАЕМА — не че планът се побира.
   *
   * Двустепенно нарочно: `inPlan` казва „от тази дата има смисъл да се брои", а
   * `LessonDates.complete` казва „всички поискани занятия се намериха до края". Слот може да
   * е `inPlan: true` и пак да върне непълен план (измерено: начало 2027-08-31, 30 занятия →
   * намерени 17). Тогава извикващият пада на резервния път, а R9 гарантира, че дори празният
   * резултат оставя маркер.
   */
  inPlan: boolean;
} {
  const { slotStartDate, schoolYear, yearStartDate, scopeEndDate } = input;
  const bounds = absoluteWorkYearWindow(schoolYear);

  /**
   * Липсваща дата (стар backend) → началото на годината ОТ КЛЮЧА, не от прозореца на този
   * екран (R11). Прозорецът се мести по данни и е различен на таблото и при учителя, тоест
   * легаси слот без своя дата получаваше различно първо занятие на двата екрана — същият
   * клас разминаване, който R1 обяви за затворен, само през друг вход.
   */
  const startDateOrFallback = slotStartDate || schoolYearStartDate(schoolYear) || yearStartDate;

  if (!bounds) {
    return { startDate: startDateOrFallback, endDate: scopeEndDate, inPlan: true };
  }

  const startDate = startDateOrFallback;

  /**
   * Включващо и от двете страни (R7): слот, започващ на последния ден от границите, пак има
   * право на план. Строгото `<` пращаше еднодневния курс на резервния път, където датата се
   * рисува, но БЕЗ маркер „последен час" — курс без обявен край.
   */
  const inPlan = startDate >= bounds.startDate && startDate <= bounds.endDate;
  /**
   * Краят е ОБХВАТЪТ, подаден от извикващия — същият, към който се клипват и неучебните
   * периоди. Двете граници са ЕДНА: клипвани към различни диапазони, ваканциите извън по-късия
   * стават невидими за плана и занятие пада в непотисната ваканция (R10).
   *
   * `bounds` (абсолютната ограда по ключа) остава САМО за `inPlan` — тя отговаря на „има ли
   * смисъл изобщо да се брои от тази дата", не на „докъде".
   */
  return { startDate, endDate: scopeEndDate, inPlan };
}

/**
 * Краят на РАБОТНАТА година — прагът „не се побира".
 * Липсва → 30 юни на втората календарна година от ключа (поведението отпреди картата).
 */
export function workYearEndDate(
  schoolYear: string | null | undefined,
  yearDates?: { workYearEndDate?: string } | null,
): string | null {
  const explicit = yearDates?.workYearEndDate;
  if (explicit && isRealIsoDate(explicit)) return explicit;

  const start = schoolYearStartDate(schoolYear);
  if (!start) return null;
  return `${Number(start.slice(0, 4)) + 1}-06-30`;
}

export interface DateWindow {
  startDate: string;
  endDate: string;
}

/** Двете календарни години на ключа („2026-2027" → `[2026, 2027]`); `null` при нечетим ключ. */
function yearsOf(schoolYear: string | null | undefined): [number, number] | null {
  const m = /^(\d{4})\s*[-/]\s*(\d{4})$/.exec(String(schoolYear ?? '').trim());
  return m ? [Number(m[1]), Number(m[2])] : null;
}

/**
 * Истинска календарна дата, не само правилна форма.
 *
 * `ISO_DATE` пропуска „2026-02-30", а `Date.UTC` го превърта в март — тоест дата, която
 * НЕ съществува, минава валидацията и после участва в сравненията като истинска. Същата
 * сверка, която `lessonDatesForSlot` и `dateWindowFrom` правят преди да броят.
 */
export function isRealIsoDate(value: string | null | undefined): boolean {
  const m = ISO_DATE.exec(String(value ?? ''));
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    !Number.isNaN(probe.getTime()) &&
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
}

/**
 * Ключът на учебната година, изведен от ДАТА („2026-2027").
 *
 * Правилото е септемврийско: от 1 септември нататък годината започва в текущата календарна,
 * преди това — в миналата. Локални части, не UTC: границата е местна дата, а не момент.
 *
 * 🔴 Съществува, за да няма закована константа. `useActiveSchoolYear` връщаше `'2025-2026'`
 * винаги когато настройките са недостъпни — тоест при всеки първи render и при всяка грешка
 * на snapshot-а админският календар се заковаваше за 2025-2026, и с всяка изминала година
 * резервът ставаше по-грешен, без нищо да го каже. Същият дефект, който `deriveSchoolYearFromDate`
 * поправи от страната на сървъра.
 *
 * Резерв, не работен път: съхранената активна година печели винаги, когато я има.
 */
export function schoolYearKeyFromDate(today: Date): string {
  const startYear = today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1;
  return `${startYear}-${startYear + 1}`;
}

/**
 * Прозорецът, в който НАЧАЛОТО на учебната година има право да падне: 1 август – 31 октомври
 * на ПЪРВАТА календарна година от ключа.
 *
 * Решение на owner (2026-09-05). Задачата оставяше границата отворена; избран е тесният
 * вариант, защото той отказва и разменен месец („07.03.2026" вместо „03.07.2026"), не само
 * явно грешната година. Началото на учебната година е входът, от който тръгва ВСЯКА сметка
 * надолу — сгрешено, то мести всяко занятие на всеки курс.
 */
export function schoolYearStartWindow(schoolYear: string | null | undefined): DateWindow | null {
  const years = yearsOf(schoolYear);
  if (!years) return null;
  return { startDate: `${years[0]}-08-01`, endDate: `${years[0]}-10-31` };
}

/**
 * Прозорецът, в който КРАЯТ на учебната година има право да падне: ВТОРАТА календарна
 * година от ключа (R1).
 *
 * По-широк от прозореца на началото нарочно. Началото е тясно (1 авг – 31 окт), защото
 * знаем кога започва учебна година; краят зависи от това колко седмици учи училището и
 * не бива да се стеснява по усещане. Календарната година на ключа е точно оградата, която
 * R1 иска: тя хваща сгрешена година („2077"), без да съди за месеца.
 */
/**
 * ХОРИЗОНТЪТ на учебната година — 31 декември на втората календарна година.
 *
 * 🔴 ЕДИН израз, защото това е ЕДНО понятие: „нищо в записите на тази учебна година не стига
 * отвъд тази дата". Дотук числото стоеше като ДВА отделни литерала — в `schoolYearEndWindow` и в
 * `absoluteWorkYearWindow` — и това правеше всяко правило, което ги сравнява, вярно ПО СЪВПАДЕНИЕ.
 *
 * Мутационен кръг го показа върху собствения ми гард: тест, който твърди „рамката се мери срещу
 * СВОЯ таван", остана ЗЕЛЕН, когато рамката беше пренасочена към чуждия — защото двете стойности
 * съвпадат. Черна кутия не може да различи окабеляване, чиито два изхода са равни. Затова
 * съвпадението се МАХА, вместо да се пази с тест: оттук нататък двете са равни ПО КОНСТРУКЦИЯ, а
 * разделянето им иска промяна в ТОЗИ израз, тоест е видимо.
 *
 * Класът е познат на файла: четирите хоризонта (549 / 365 / 400 / 31 декември) се разминаха с до
 * пет месеца, защото всеки беше свой литерал.
 */
export function schoolYearHorizonEnd(schoolYear: string | null | undefined): string | null {
  const years = yearsOf(schoolYear);
  return years ? `${years[1]}-12-31` : null;
}

export function schoolYearEndWindow(schoolYear: string | null | undefined): DateWindow | null {
  const years = yearsOf(schoolYear);
  const хоризонт = schoolYearHorizonEnd(schoolYear);
  if (!years || !хоризонт) return null;
  return { startDate: `${years[1]}-01-01`, endDate: хоризонт };
}

/**
 * 🔴 РАМКАТА на учебната година — единствената граница на курсовете (C4 R1/R2).
 *
 * Дотук тук стояха ЧЕТИРИ различни хоризонта (549 дни за плана, 365 за данните, 400 за
 * маркерите, 31 декември за валидацията), измерени във Фаза 1 и разминаващи се с до пет
 * месеца. Решението на owner-а ги сведе до един: учебната година има въведени начало и
 * край, и всичко извън тях се блокира при въвеждане.
 *
 * `null`, когато краят не е въведен — и това НЕ е резерв, а отсъствие на рамка. Краят не
 * се извежда (R1); система, която не знае кога свършва годината, не може да твърди, че
 * един курс не се побира в нея. Извикващият решава какво значи липсата: валидацията пада
 * на абсолютната ограда срещу печатна грешка, а блокирането не се задейства изобщо.
 *
 * Началото има резерв (1 септември от ключа), защото него го има отвсякъде — от ключа,
 * от настройките, и от изключението на категорията.
 */
export function schoolYearWindow(
  schoolYear: string | null | undefined,
  yearDates?: { schoolYearStartDate?: string; schoolYearEndDate?: string } | null,
): DateWindow | null {
  const end = yearDates?.schoolYearEndDate;
  if (!end || !isRealIsoDate(end)) return null;

  /**
   * 🔴 НАЧАЛОТО се проверява при четене СЪЩО — симетрично на края.
   *
   * Първата версия на клипването пазеше само края и наборът на гарда носеше едно и също
   * валидно начало във всичките си единайсет случая. Тоест твърдението „обхватът се съдържа в
   * резерва" беше упражнено само по единия ръб, а другият падаше: заварен запис с начало
   * `2020-01-01` дава рамка оттам, съюзът взима МИНИМУМА на началата, и обхватът излиза
   * НАЛЯВО от резерва — тоест учителят вижда по-малко от админа, точно посоката, която целият
   * този резерв съществува да направи невъзможна.
   *
   * Правилото, извлечено от това: щом едно твърдение има ДВЕ неравенства, наборът трябва да
   * съдържа случай, който чупи всяко от тях поотделно. Иначе половината асерт е декор.
   *
   * Само ИЗРИЧНОТО начало се проверява — резервът (1 септември от ключа) е в прозореца по
   * построение.
   */
  const explicitStart = yearDates?.schoolYearStartDate;
  if (explicitStart && isRealIsoDate(explicitStart)) {
    const allowedStart = schoolYearStartWindow(schoolYear);
    if (allowedStart && (explicitStart < allowedStart.startDate || explicitStart > allowedStart.endDate)) {
      return null;
    }
  }
  const startDate =
    explicitStart && isRealIsoDate(explicitStart) ? explicitStart : schoolYearStartDate(schoolYear);
  if (!startDate) return null;

  // Обърната двойка не е рамка. Валидацията я отказва при запис, но заварен или пряко
  // редактиран запис може да я носи — а рамка с край преди началото би блокирала ВСЕКИ
  // курс, тоест би изглеждала като счупена система, не като счупен запис.
  if (startDate > end) return null;

  /**
   * 🔴 Четенето проверява СЪЩОТО, което проверява записът.
   *
   * `schoolYearEndWindow` ограничава какво може да се ЗАПИШЕ, не какво може да се ПРОЧЕТЕ:
   * заварен или пряко редактиран документ може да носи край `2029-06-30` и той минаваше право
   * в рамката. Оттам нататък всяко твърдение „обхватът се съдържа в X" пада, защото X е изведен
   * от границите на ВАЛИДАЦИЯТА.
   *
   * Отговорът е `null`, не клипване: краят е ВЪВЕДЕН (R1), и стойност, която системата не би
   * приела, не е въведена стойност. Без рамка блокирането просто не се задейства — по-добре,
   * отколкото курсове да се отказват спрямо граница, която никой не е задавал.
   */
  const allowed = schoolYearEndWindow(schoolYear);
  if (allowed && (end < allowed.startDate || end > allowed.endDate)) return null;

  return { startDate, endDate: end };
}

/**
 * Абсолютната граница, в която работната година има право да съществува:
 * 1 юли на първата – 31 декември на втората календарна година.
 *
 * Без нея печатна грешка в годината („2077") минава всяка относителна проверка: тя е
 * съгласувана сама със себе си (край ≥ начало ≥ учебна), а работната година е точно
 * прозорецът, по който R9 отказва невъзможни ваканции. Относителните проверки хващат
 * разменени полета; тази хваща сгрешена година.
 */
export function absoluteWorkYearWindow(schoolYear: string | null | undefined): DateWindow | null {
  const years = yearsOf(schoolYear);
  const хоризонт = schoolYearHorizonEnd(schoolYear);
  if (!years || !хоризонт) return null;
  // Горният ръб е ХОРИЗОНТЪТ, не свой литерал — виж `schoolYearHorizonEnd`.
  return { startDate: `${years[0]}-07-01`, endDate: хоризонт };
}

/**
 * Откъде идва горният ръб на прозореца — влиза в отказа, за да е УКАЗАНИЕ.
 *
 * 🔴 Четири стойности, не две. Прозорецът е СЪЮЗ: базовият кандидат може да НЕ Е максимумът, и
 * тогава съобщението описваше друго число, а не онова, което е отказало датата. Измерено:
 * работна година до `2027-09-30` при невъведена следваща → прозорецът свършва `2027-09-30`, а
 * източникът твърдеше `2027-08-31` „резерв; въведете следващата година" — указание към задънена
 * улица, защото `2027-09-14 < 2027-09-30` и въвеждането не мени нищо.
 *
 * 🔴 **Всяка стойност назовава СВОЯ лост** — какво да направи човекът, за да мръдне тази
 * граница. Затова стойностите не са „кой кандидат", а „коя ЧОВЕШКА ситуация": един и същ
 * кандидат (резервът) стои зад две различни ситуации с две различни указания, а въведена
 * стойност, прерязана от тавана, изобщо няма лост.
 *
 * | стойност | границата идва от | лостът |
 * |---|---|---|
 * | `next-year-start` | въведеното начало на следващата година | запиши остатъка на следващата |
 * | `fallback` | резервът `${y2}-08-31`, защото начало ЛИПСВА или е извън оградата | въведи го |
 * | `fallback-earlier` | резервът, макар начало да Е въведено и валидно — то е ПО-РАНО | няма: резервът е по-щедрият |
 * | `school-year-frame` | въведения край на учебната година | удължи го |
 * | `work-year` | въведения край на работната година | удължи го |
 * | `absolute-cap` | СОБСТВЕНИЯ таван на кандидата — рамка или работна, опряла в него | няма: това е максимумът |
 *
 * 🔴 **`absolute-cap` значи „кандидатът е на СВОЯ таван", не „прерязан от абсолютната ограда".**
 * Обхватът му е по-широк от първото име: покрива и ПРЕРЯЗАНАТА работна година (`>`), и записаната
 * ТОЧНО на тавана (`==`), и рамката, която изобщо не се клипва, но може да е на своя. Кой е „своят"
 * казва `candidateCap` — два ОТДЕЛНИ литерала, които днес дават една и съща стойност.
 *
 * ⚠️ `fallback-earlier` и `absolute-cap` съществуват заради едно и също: изречение, което сочи
 * лост, вече изчерпан, праща админа в задънена улица.
 *
 * ⚠️ И двете са достижими само при вече нереалистични данни (`2077-12-31` заварена грешка,
 * `2027-12-31` легален максимум, който според owner-а никое училище няма). Границата се пази,
 * защото таксономията иначе твърди разлика, каквато измерването не показва — не защото потребител
 * ще я срещне.
 */
export type HolidayEdgeSource =
  | 'next-year-start'
  | 'fallback'
  | 'fallback-earlier'
  | 'school-year-frame'
  | 'work-year'
  | 'absolute-cap';

/**
 * 🔴 ПРАВИЛО ЗА РАВЕНСТВО — обявено, не изведено от оператор.
 *
 * Когато два кандидата дават ЕДНА И СЪЩА граница, печели онзи, който идва от ВЪВЕДЕНА стойност.
 * Резервът се назовава само когато наистина е заместил нещо липсващо.
 *
 * Без него строгото `>` дава резерва на всяко равенство — а равенството НЕ е екзотика, то е
 * подразбиращото се състояние: `schoolYearStartDate('2027-2028')` е `2027-09-01` (изведено от
 * ключа), тоест денят преди него е `2027-08-31` — точно резервът. Админ, въвел каноничното
 * 1 септември, получаваше „началото на следващата година не е въведено. Проверете го" за поле,
 * което е попълнено и вярно.
 *
 * Прилага се на ДВЕТЕ места, където се избира между кандидати: тук (`holidayWindowUpperEdge`)
 * и в съюза (`holidayWindow`). Правило, приложено наполовина, вече ни е струвало кръг.
 */
const еРезерв = (s: HolidayEdgeSource): boolean => s === 'fallback' || s === 'fallback-earlier';

/**
 * Източниците, чието изречение НАЗОВАВА началото на следващата учебна година.
 *
 * 🔴 ЕДИН израз за сървъра и за клиента. Сървърът решава по него дали да ИЗПРАТИ датата,
 * клиентът — дали да я ИЗИСКА, преди да построи изречение с нея. Две отделни проверки на един
 * въпрос се разминават при първото добавяне на стойност: сървър, който не праща датата, докато
 * клиентът вече я е поискал, дава `unknown` и изречение без причина — при зелени набори от двете
 * страни, защото всяка проверява своята половина.
 */
export const edgeSourceNamesNextYearStart = (s: HolidayEdgeSource): boolean =>
  s === 'next-year-start' || s === 'fallback-earlier';

/**
 * Таванът на ЕДИН кандидат — всеки срещу СВОЯ, никога срещу общ.
 *
 * Всеки източник пита СВОЯТА функция за прозорец. Двата горни ръба днес съвпадат, но вече ПО
 * КОНСТРУКЦИЯ: и двете ги вземат от `schoolYearHorizonEnd`, тоест няма втори литерал, който да
 * мръдне сам. Дотук бяха два отделни литерала и всяко правило, което ги сравнява, беше вярно по
 * съвпадение — включително гардът, който го твърдеше (остана зелен при пренасочена рамка).
 *
 * `null` значи „този кандидат няма таван, до който да опре". Базовият е такъв и това е ИЗМЕРЕНО,
 * не допуснато: `валидноНачало ≤ ограда.endDate`, тоест `денПреди ≤ ${y2}-12-30` — помитане по
 * 11 стойности (включително извън оградата и нечетима) дава максимум `2027-12-30` за `2026-2027`.
 */
export function candidateCap(
  source: HolidayEdgeSource,
  schoolYear: string | null | undefined,
): string | null {
  switch (source) {
    case 'school-year-frame':
      return schoolYearEndWindow(schoolYear)?.endDate ?? null;
    case 'work-year':
      return absoluteWorkYearWindow(schoolYear)?.endDate ?? null;
    default:
      return null;
  }
}

/**
 * Кандидат, опрял в СВОЯ таван, няма лост — и затова сменя източника си на `absolute-cap`.
 *
 * `>=`, не `>`: същото правило за равенство, което важи при избора между кандидати. Дотук само
 * ПРЕРЯЗАНАТА работна година (`>`) губеше лоста си, а работна, записана точно на тавана
 * (`${y2}-12-31` — легалната максимална стойност, `systemSettings.ts` отказва по-голяма), оставаше
 * `work-year` с указание „удължете края", което сървърът отказва.
 *
 * ⚠️ Достижимо само при вече нереалистични данни: owner потвърди, че работна година, започнала
 * през септември, не свършва през декември на следващата. Записано като граница, не като дефект,
 * който потребител ще срещне.
 */
function наСвояТаван(
  source: HolidayEdgeSource,
  endDate: string,
  schoolYear: string | null | undefined,
): boolean {
  const таван = candidateCap(source, schoolYear);
  return таван !== null && endDate >= таван;
}

/**
 * 🔴 Горният ръб на прозореца за ваканции — ЕДИН израз за прозореца И за отказа.
 *
 * Отказът трябва да казва ОТ КОЕ идва границата („защото 2027-2028 започва на 15.09.2027" срещу
 * „защото началото на 2027-2028 не е въведено"), иначе поправката изглежда неслучила се: докато
 * следващата година не е въведена, резервът е същият 31 август и отказът е дословно старият.
 *
 * Ако съобщението пресмяташе източника сам, това щеше да е ВТОРО копие на `max` — правилото,
 * което тази верига затваря от седмица: правило се преразказва, израз се вика.
 */
export function holidayWindowUpperEdge(
  schoolYear: string | null | undefined,
  nextYearStartDate?: string | null,
): { endDate: string; source: HolidayEdgeSource } | null {
  const years = yearsOf(schoolYear);
  if (!years) return null;   // `''` сортира ПОД всяка ISO дата — не се връща дата, която не е
  const резерв = `${years[1]}-08-31`;

  /**
   * 🔴 Началото на следващата се КЛИПВА до абсолютната ограда — както работната година.
   *
   * Това беше единственият кандидат без клипване, и заварен запис `2077-09-01` даваше прозорец
   * до `2077-08-31`. Следствието е записано в собствения ни код: `teacherCalendar` твърди, че
   * резервът е ДОКАЗУЕМО надмножество на сървърния обхват — тоест учителят щеше да вижда
   * по-малко от админа, дефектът на C2 R5, затворен преди дни.
   */
  const ограда = absoluteWorkYearWindow(schoolYear);
  const валидноНачало =
    nextYearStartDate &&
    isRealIsoDate(nextYearStartDate) &&
    (!ограда || (nextYearStartDate >= ограда.startDate && nextYearStartDate <= ограда.endDate))
      ? nextYearStartDate
      : null;

  const отСледващата = валидноНачало ? денПреди(валидноНачало) : null;

  // Начало ЛИПСВА или е извън оградата — резервът замества нещо, тоест указанието „въведи го" е
  // истинско. Това е единственият клон, в който то е истинско.
  if (!отСледващата) return { endDate: резерв, source: 'fallback' };

  // `>=`, не `>` — ПРАВИЛОТО ЗА РАВЕНСТВО (виж `еРезерв`). При еднаква граница печели ВЪВЕДЕНАТА
  // стойност: числото е същото, но изречението назовава нея, вместо да иска попълване на поле,
  // което е попълнено.
  if (отСледващата >= резерв) return { endDate: отСледващата, source: 'next-year-start' };

  // Въведено, валидно и ПО-РАНО от резерва — `max` легитимно оставя резерва. Своя стойност,
  // защото тук „въведете началото" е задънена улица: то е въведено, а резервът е по-щедрият.
  return { endDate: резерв, source: 'fallback-earlier' };
}

/**
 * Началото на СЛЕДВАЩАТА учебна година от картата с настройки — един израз за шестте повиквания.
 *
 * 🔴 Съществува, за да не строи всеки консуматор ключа сам. Ключът е `${y2}-${y2+1}`, изведен от
 * ТЕКУЩИЯ (не от часовника): сесия, която го смята от датата, дава грешната година между смяната
 * в настройките и 1 септември — същият дефект, който `slotStartDate` вече документира.
 *
 * `null`, когато следващата година я няма в настройките или началото ѝ не е реална дата. Тогава
 * ръбът пада на резерва; виж `holidayWindow`.
 */
export function nextYearStartFrom(
  schoolYears: Record<string, { schoolYearStartDate?: string } | null | undefined> | null | undefined,
  schoolYear: string | null | undefined,
): string | null {
  const years = yearsOf(schoolYear);
  if (!years || !schoolYears) return null;
  const ключ = `${years[1]}-${years[1] + 1}`;
  const начало = schoolYears[ключ]?.schoolYearStartDate;
  return начало && isRealIsoDate(начало) ? начало : null;
}

/** ISO дата минус един ден; `null` при нечетима стойност. */
function денПреди(iso: string): string | null {
  if (!isRealIsoDate(iso)) return null;
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}-${m}-${day}`;
}

export interface HolidayWindow extends DateWindow {
  /** Кой кандидат е произвел ГОРНАТА граница — отказът назовава него, не базовия ръб. */
  edgeSource: HolidayEdgeSource;
}

/**
 * Прозорецът, в който ваканция има право да съществува (R9), разширен до НАДМНОЖЕСТВО (C4 R2).
 *
 * Задачата му е да отказва НЕВЪЗМОЖНОТО („2020-01-01 … 2077-12-31"), не да бъде точен.
 * Валидация, която отхвърля истински данни, се заобикаля от потребителя и спира да пази.
 *
 * 🔴 Затова тук се взима СЪЮЗЪТ на всичко известно, а нищо не стеснява:
 *  · административната година (1 авг – 31 авг на втората) — резервът;
 *  · рамката на учебната година, когато краят ѝ е въведен (R2: няма дата с занятие, на
 *    която ваканция да не може да бъде записана);
 *  · работната година, когато е въведена.
 *
 * ⚠️ Дотук въведената работна година СТЕСНЯВАШЕ прозореца („тя е по-тясна и по-точна").
 * Това е обърнато нарочно и ето с какво се мери: училище с работна година до `2027-07-09`
 * не можеше да запише лятната си ваканция `2027-07-03 … 2027-08-31` — легитимен запис на
 * приключилата година, който `schoolYearOfMonth` води именно към нея. Точността тук няма
 * стойност: ваканция извън работната година не вреди на никого, а невъзможността да бъде
 * записана оставя цялото лято непокрито и ражда призрачни занятия в декларациите.
 *
 * Горният ръб стига до 31 АВГУСТ на втората календарна година (не 31 юли): лятната
 * ваканция свършва в края на август, а септември вече принадлежи на следващата година
 * (`schoolYearOfMonth`). Застъпването с прозореца на следващата година е приемливо —
 * това е ограда срещу печатни грешки, не дял.
 */
export function holidayWindow(
  schoolYear: string | null | undefined,
  yearDates?: {
    schoolYearStartDate?: string;
    schoolYearEndDate?: string;
    workYearStartDate?: string;
    workYearEndDate?: string;
  } | null,
  /** Началото на СЛЕДВАЩАТА учебна година (`nextYearStartFrom`); `null` → резервът 31 август. */
  nextYearStartDate?: string | null,
): HolidayWindow | null {
  const years = yearsOf(schoolYear);
  if (!years) return null;

  /**
   * 🔴 НАДМНОЖЕСТВОТО е по КОНСТРУКЦИЯ, не по уговорка (R2).
   *
   * Инвариантът: няма дата, на която курс може да има занятие, а ваканция да не може да
   * бъде записана. Обратното прави системата противоречива — админът вижда занятие, което
   * трябва да отмени, и получава отказ при отмяната.
   *
   * Не се прави с „едни и същи дати за всичко": това би стеснило прозореца до учебната
   * година и би направило ЛЯТНАТА ваканция незаписваема, а летните лагери (R8) се
   * провеждат точно в нея — с обявени учебни дни ВЪТРЕ във ваканцията, вместо тя да се
   * съкращава.
   */
  /**
   * 🔴 Горният ръб СЛЕДВА началото на следващата учебна година (C5).
   *
   * Решение на owner-а: „лятната ваканция свършва, когато започва следващата учебна година" —
   * правило, не тазгодишна случайност. Тоест ръбът е ИЗВЕДЕН, а не константа: същият клас като
   * края на годината преди C4 (производно число, ползвано за граница).
   *
   * Мерено на production: училището има ваканция `2027-07-03 … 2027-09-14`, защото учебната
   * 2027-2028 започва на 15 септември. Константата `${y2}-08-31` я отказваше — с ДВЕ СЕДМИЦИ,
   * не с ръб.
   *
   * 🔴 `max`, не просто „денят преди". Наивният вариант СВИВА прозореца при печатна грешка, и
   * то по достижим път — измерено:
   *
   *   начало на следващата | рамка | ръбът наивно | ръбът с max
   *   2027-09-15 (вярно)   | има   | 2027-09-14   | 2027-09-14   ← целта
   *   2027-06-01 (грешка)  | има   | 2027-05-31   | 2027-08-31   (съюзът и без това пази)
   *   2027-06-01 (грешка)  | НЯМА  | 🔴 2027-05-31 | 2027-08-31   ← тук naive свива с 3 месеца
   *
   * Третият ред е достижим ДНЕС: production няма въведена нито една година, а е нормално да
   * подготвиш следващата, преди да си затворил текущата. С `max` печатната грешка може само да
   * не промени нищо.
   *
   * Принципът, вече записан по-долу: прозорецът отказва НЕВЪЗМОЖНОТО, не е за точност. Точност,
   * купена с риск от свиване, е лоша сделка тук.
   *
   * ⚠️ Цена: училище, което наистина започва през юни, ще може да запише ваканция до 31 август.
   * Ваканция извън годината не вреди на никого; невъзможността да я запишеш оставя лято непокрито.
   */
  const ръб = holidayWindowUpperEdge(schoolYear, nextYearStartDate);
  if (!ръб) return null;

  /**
   * 🔴 Кандидатите носят ИЗТОЧНИКА си, за да може отказът да назове онзи, който Е произвел
   * границата. Дотук съобщението питаше базовия ръб, а максимумът можеше да е друг кандидат —
   * тоест обясняваше едно число с причината на друго.
   */
  const candidates: Array<DateWindow & { source: HolidayEdgeSource }> = [
    { startDate: `${years[0]}-08-01`, endDate: ръб.endDate, source: ръб.source },
  ];

  const frame = schoolYearWindow(schoolYear, yearDates);
  if (frame) {
    // Рамката НЕ се клипва (`schoolYearWindow` връща `null` при край извън допустимото), но пак
    // може да е записана ТОЧНО на своя таван — и тогава лостът ѝ също е изчерпан.
    candidates.push({
      ...frame,
      source: наСвояТаван('school-year-frame', frame.endDate, schoolYear)
        ? 'absolute-cap'
        : 'school-year-frame',
    });
  }

  /**
   * 🔴 Работната година се КЛИПВА до абсолютните си граници при ЧЕТЕНЕ.
   *
   * `absoluteWorkYearWindow` пази какво се записва, не какво се чете. Заварен запис с работна
   * до `2077-12-31` иначе разгъва обхвата на **18 780 дни** — и това не е хипотеза: коментарът
   * в `AdminPage` носи измерването на същата стойност (~18 700 събития, замръзнало табло).
   * Клипването е защитата, заради която обхватът изобщо съществува; съюзът не бива да я
   * заобикаля.
   *
   * Клипва се, а не се изхвърля: дълга работна година е намерение на админа, просто ограничено
   * до възможното.
   */
  const absolute = absoluteWorkYearWindow(schoolYear);
  const workStart = yearDates?.workYearStartDate;
  const workEnd = yearDates?.workYearEndDate;
  if (
    absolute &&
    workStart && isRealIsoDate(workStart) &&
    workEnd && isRealIsoDate(workEnd) &&
    workStart <= workEnd &&
    // 🔴 Кандидат, който НЕ СЕ ПРЕСИЧА с абсолютния прозорец, ОТПАДА — не се клипва.
    // Клипване на двата ръба поотделно превръща работна `2020-01-01 … 2020-12-31` в обърната
    // двойка (начало `2026-07-01`, край `2020-12-31`), а съюзът после взима началото ѝ и
    // издърпва целия прозорец назад. Безсмислена работна година не бива да мести нищо.
    workStart <= absolute.endDate &&
    workEnd >= absolute.startDate
  ) {
    /**
     * 🔴 Прерязан ГОРЕ значи, че границата идва от ТАВАНА, не от въведения край — и оттам друг
     * лост. „Удължете края на работната година" е задънена улица, когато той ВЕЧЕ е отвъд тавана:
     * заварено `workYearEndDate: 2077-12-31` дава граница `2027-12-31` и указание да се удължи
     * нещо, което е с петдесет години по-напред.
     *
     * Долният ръб не сменя източника: `edgeSource` описва ГОРНАТА граница, тя е тази, която
     * отказва ваканция.
     */
    const край = workEnd > absolute.endDate ? absolute.endDate : workEnd;
    candidates.push({
      startDate: workStart < absolute.startDate ? absolute.startDate : workStart,
      endDate: край,
      // `наСвояТаван` покрива и прерязания (`>`), и записания точно на тавана (`==`) — едно
      // правило, не два случая.
      source: наСвояТаван('work-year', край, schoolYear) ? 'absolute-cap' : 'work-year',
    });
  }

  /**
   * ПРАВИЛОТО ЗА РАВЕНСТВО и тук — вторият от двата избора между кандидати.
   *
   * Строгото `>` дава победата на ПЪРВИЯ по ред, а първи е базовият кандидат. Тоест при еднаква
   * граница резервът надговаряше въведена стойност само защото стои по-отпред в масива — редът
   * на литерал не е аргумент за това чие е числото.
   */
  const победител = candidates.reduce(
    (a, c) =>
      c.endDate > a.endDate || (c.endDate === a.endDate && еРезерв(a.source) && !еРезерв(c.source))
        ? c
        : a,
    candidates[0],
  );
  return {
    startDate: candidates.reduce((a, c) => (c.startDate < a ? c.startDate : a), candidates[0].startDate),
    endDate: победител.endDate,
    edgeSource: победител.source,
  };
}

/**
 * Прозорецът, в който НАЧАЛОТО на курс има право да падне (C4 R2).
 *
 * 🔴 Рамката на учебната година, когато краят ѝ е въведен — една граница за всички курсове.
 *
 * Дотук тук печелеше РАБОТНАТА година, а резервът беше абсолютната ограда до 31 декември на
 * втората календарна година, тоест курс от 2026-2027 можеше да започне през ноември 2027.
 * Границата 31 декември дойде от оградата на РАБОТНАТА година (срещу печатна грешка от рода
 * на „2077") и попадна при курсовете през указание — тя няма отношение към това кога учи
 * училището. След C4 работната година остава само за персонала (задача D).
 *
 * Без въведен край няма рамка (`schoolYearWindow` → `null`) и се пада на ПРОЗОРЕЦА ЗА ВАКАНЦИИ.
 *
 * 🔴 Не на `absoluteWorkYearWindow`, макар той да е „оградата срещу печатна грешка". Той стига до
 * 31 декември, а прозорецът за ваканции — до 31 август: тоест курс с начало `2027-09-15` се
 * приемаше, а ваканция върху СЪЩАТА дата се отказваше. Точно противоречието, което R2 съществува
 * да забрани, само в конфигурацията без въведен край — тоест в тази, която работи ДНЕС.
 *
 * Прозорецът за ваканции е надмножество на рамката по построение, тоест този резерв запазва
 * инварианта и когато рамка няма.
 */
export function categoryStartWindow(
  schoolYear: string | null | undefined,
  yearDates?: {
    schoolYearStartDate?: string;
    schoolYearEndDate?: string;
    workYearStartDate?: string;
    workYearEndDate?: string;
  } | null,
  /** Виж `holidayWindow` — резервът пада на прозореца за ваканции, тоест ръбът го следва. */
  nextYearStartDate?: string | null,
): DateWindow | null {
  return schoolYearWindow(schoolYear, yearDates) ?? holidayWindow(schoolYear, yearDates, nextYearStartDate);
}

/**
 * Прозорецът от една година напред: началната дата → същата дата след година,
 * минус ден. За 1 септември 2026 това е 31 август 2027.
 *
 * Защо цяла година, а не до 30 юни: курс с много седмици и много ваканции може
 * да свърши СЛЕД края на учебната година, и календарът трябва да го покаже
 * (R4). Лятото след нея е достатъчният запас — по-дълъг прозорец би надхвърлил
 * тавана на разгъването и маркерите пак биха се разминали с потискането.
 */
export function dateWindowFrom(startDate: string): DateWindow | null {
  const m = ISO_DATE.exec(String(startDate || ''));
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  // Същата сверка като в `lessonDatesForSlot`: `ISO_DATE` пропуска „2026-02-30",
  // а `Date.UTC` го превърта в март. Прозорец от несъществуваща дата после
  // участва в сравненията на `clipRangeToWindow` като че ли е истинска.
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    Number.isNaN(probe.getTime()) ||
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }
  const end = new Date(Date.UTC(year + 1, month - 1, day));
  end.setUTCDate(end.getUTCDate() - 1);
  return {
    startDate,
    endDate: `${end.getUTCFullYear()}-${pad2(end.getUTCMonth() + 1)}-${pad2(end.getUTCDate())}`,
  };
}

/**
 * Първият ден на учебната година, изведен от КЛЮЧА ѝ („2026-2027" или
 * „2026/2027") → „2026-09-01".
 *
 * Ключът, а не часовникът: ваканциите и слотовете се теглят по СЪХРАНЕНАТА
 * активна година, а стенният часовник се превърта на 1 септември независимо от
 * нея. Разминат ли се, прозорецът изрязва всичко изтеглено и слоят с ваканциите
 * изчезва без грешка (мерено 2026-09-04: 0 маркера при година 2025-2026 и
 * часовник в 2026/2027).
 *
 * `null` при ключ, от който не се чете четирицифрена начална година.
 */
export function schoolYearStartDate(schoolYear: string | null | undefined): string | null {
  const m = /^(\d{4})\s*[-/]\s*\d{4}$/.exec(String(schoolYear ?? '').trim());
  return m ? `${m[1]}-09-01` : null;
}

/**
 * Пресича неучебен период с прозореца.
 *
 * Причината да съществува: `createHoliday` валидира само `endDate ≥ startDate`
 * и НЕ ограничава дължината. Запис „2020-01-01 … 2077-12-31" (двустранна
 * печатна грешка) иначе стига до всеки консуматор в цялата си дължина, а те го
 * съкращават по РАЗЛИЧНИ правила — едно реже на 400 дни от началото, друго
 * изобщо не реже — и после показват различни неучебни дни. Клипването ги връща
 * на един и същ, винаги ограничен списък.
 *
 * `null` = периодът не се пресича с прозореца, или е повреден (липсваща дата,
 * край преди начало). Такъв период не влиза никъде.
 */
export function clipRangeToWindow(
  range: NonWorkingRange,
  window: DateWindow,
): NonWorkingRange | null {
  if (!range || !range.startDate || !range.endDate) return null;
  if (range.startDate > range.endDate) return null;
  const startDate = range.startDate > window.startDate ? range.startDate : window.startDate;
  const endDate = range.endDate < window.endDate ? range.endDate : window.endDate;
  return startDate > endDate ? null : { startDate, endDate };
}

/**
 * Таван на прегледаните дни. 52 занятия × 7 дни = 364; остатъкът е мястото,
 * с което неучебните дни изместват края. Диапазон, който покрива всичко, спира
 * тук с НЕПЪЛЕН резултат вместо да върти вечно — извикващият го вижда по
 * `complete: false`. Същата причина като `guard < 400` в `expandHolidayDates`.
 */
const MAX_DAYS_SCANNED = 800;

/** Отказана обявена дата — стойността и ПРИЧИНАТА, за да я каже екранът. */
export interface RejectedDeclaredDay {
  date: string;
  reason:
    | 'unreadable'
    | 'outside-year'
    | 'wrong-weekday'
    | 'duplicate'
    | 'too-many'
    /** Слотът няма четим ден от седмицата — отказът е за СЛОТА, не за датата (виж по-долу). */
    | 'unknown-slot-day'
    /** Полето изобщо не е списък. Носи се с празна `date` — отказът е за ВХОДА, не за стойност. */
    | 'not-a-list';
}

/** Резултатът от нормализирането: какво влиза и какво е отказано, с причина. */
export interface DeclaredDaysResult {
  accepted: string[];
  rejected: RejectedDeclaredDay[];
}

/**
 * До 10 обявени дни на учебна година (owner, 07.09) — таванът тук е НА СЛОТ и е двойно по-широк.
 *
 * Не е продуктово правило, а ограда: списък без таван е същият клас като работната година до
 * `2077`, която разгъна обхвата на 18 780 дни и замрази таблото. Двайсет е достатъчно широко, за
 * да не спъне реален случай, и достатъчно тясно, за да не може едно поле да произведе хиляди
 * итерации в разгъвача.
 */
export const MAX_DECLARED_SCHOOL_DAYS = 20;

/**
 * Таван на ВХОДА — различен от `MAX_DECLARED_SCHOOL_DAYS` и с друга задача.
 *
 * Онзи пази какво се ЗАПИСВА; този пази какво се ОБХОЖДА. Без него сто хиляди елемента минават
 * през нормализирането и се връщат в `details` на грешката — а callable-ът няма rateLimit.
 *
 * 🔴 Пет пъти таванът на приетите — и това НЕ значи частично приемане. `updateScheduleSlot`
 * отказва ЦЯЛАТА заявка при поне една отказана дата (`rejected.length > 0`), тоест 25 обявени дни
 * не записват 20 и не отхвърлят 5: не записват нищо. (Предишната редакция на този блок обещаваше
 * обратното — „вместо отказ на цялата заявка" — за поведение, невъзможно по конструкция. Класът
 * „обещание без покритие", този път в коментара на собствената си константа.)
 *
 * Тогава защо е по-широк? За да РАЗЛИЧАВА двата случая в отговора: 25 дати получават отказ по
 * ДАТИ, всяка с причина (петте излишни носят `too-many`, тоест админът вижда къде е таванът);
 * сто хиляди получават груб отказ по ДЪЛЖИНА, без списък. Тесен входен таван би слял двете в
 * едно неинформативно „входът е твърде дълъг".
 */
export const MAX_DECLARED_SCHOOL_DAYS_INPUT = MAX_DECLARED_SCHOOL_DAYS * 5;

/**
 * ЗАВАРЕНИТЕ обявени дни, направени безопасни за ЧЕТЕНЕ.
 *
 * 🔴 Съхранената стойност е НЕДОВЕРЕНА: друг писач (конзолна редакция, бъдеща функция) може да е
 * оставил там каквото и да е, а низът `'abc'` има `.length` 3 и минава наивна проверка.
 * `('abc' || []).filter` хвърля TypeError — тоест несаниран прочит сваля цялата заявка.
 *
 * Живее ТУК, а не при единия си повикващ, защото са ДВА: пътят за ЗАПИС (`updateScheduleSlot`,
 * отговорът) и пътят за ЧЕТЕНЕ (`loadTeacherSlots`, планът). Копие на второто място щеше да е
 * същият дефект, само отложен — вече го платихме веднъж, когато санирахме само записа.
 *
 * Филтрира, вместо да отказва — както `lessonDatesForSlot` изхвърля нечетимата дата и продължава
 * сметката: една повредена стойност не бива да срине операция, която не я е докоснала. Нула
 * четими значи ОТСЪСТВИЕ, не празен масив.
 */
export function readableDeclaredDays(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const strings = value.filter((v): v is string => typeof v === 'string');
  return strings.length > 0 ? strings : undefined;
}

/**
 * 🔴 ЕДИН израз за приемането на обявени учебни дни. Днес единственият му повикващ е
 * `updateScheduleSlot`.
 *
 * ⚠️ Създаването НЕ го вика — и не може: `CreateScheduleSlotRequest` няма `declaredSchoolDays`,
 * тоест по този път полето е недостижимо. Предишната редакция на този блок твърдеше „вика се и
 * при създаване" — обещание без покритие, същият клас, който веригата затваря от седмица.
 * Гардът за твърдението е поведенчески („създаването НЕ записва declaredSchoolDays",
 * `functions/src/__tests__/scheduleSlotManagement.test.ts`): добави ли някой полето към
 * създаването, тестът пада и въпросът „кой го валидира" се задава на място, вместо да се пропусне.
 *
 * Причината да е израз, а не правило: `updateScheduleSlot` строи `updateData` САМО от познати
 * полета и мълчаливо изхвърля непознатото (документирано в CLAUDE.md — така `assistantClassIds`
 * стоеше празно на 18 от 18 учителя). Правило, преразказано на две места, се разминава при
 * първата промяна; един израз не може.
 *
 * Отказите носят ПРИЧИНА, а не само брой: „датата не е приета" без причина праща админа да гадае
 * между четири различни грешки.
 *
 * ⚠️ НЕ проверява дали датата пада във ваканция. Обявяване на ден, който и без това е учебен, е
 * безобидно (нищо не отменя) и отказът му би изисквал зареждане на ваканциите тук — тоест същият
 * израз би станал зависим от данни, вместо от вход. Съзнателно оставено.
 */
export function normalizeDeclaredSchoolDays(
  dates: readonly string[] | null | undefined,
  opts: { dayOfWeek: string; frame?: DateWindow | null },
): DeclaredDaysResult {
  const accepted: string[] = [];
  const rejected: RejectedDeclaredDay[] = [];
  const dow = calendarDayToDow(opts.dayOfWeek);
  const seen = new Set<string>();

  // Втора линия срещу недоверен вход. ПЪРВАТА е в handler-а (`updateScheduleSlot` отказва с
  // `invalid-argument`, както прави за всяко съседно поле) — тя дава верния HTTP код и таван на
  // дължината. Тук стои само това, което пази ИЗРАЗА да не гръмне при друг повикващ: `for…of`
  // върху число хвърля TypeError, а гол низ би се обходил по БУКВИ и би дал десет отказа.
  //
  // 🔴 Немасивът излиза като ОТКАЗ, не като празен резултат. Празният резултат е неразличим от
  // „подаден празен списък", а той в `updateScheduleSlot` значи `FieldValue.delete()` — тоест
  // втори повикващ, какъвто този докблок изрично кани, би ИЗТРИЛ обявените дни с невалиден вход,
  // мълчаливо. `null`/`undefined` остават легитимен празен вход: те са отсъствие, не боклук.
  if (dates !== null && dates !== undefined && !Array.isArray(dates)) {
    return { accepted: [], rejected: [{ date: '', reason: 'not-a-list' }] };
  }
  const входни: readonly string[] = Array.isArray(dates) ? dates : [];

  // Н1: денят на слота НЕ зависи от нито една дата, тоест мястото на проверката е ПРЕДИ цикъла.
  // Вътре в него тя се състезаваше с `outside-year` и губеше: слот с нечетим ден плюс дата извън
  // рамката докладваше „датата е извън годината" и пращаше админа да поправя ДАТАТА вместо
  // СЛОТА — точно заблудата, заради която `unknown-slot-day` съществува.
  if (dow === undefined) {
    return {
      accepted: [],
      rejected: входни.map((raw) => ({
        date: String(raw || '').trim(), // `||`, както в цикъла — иначе `0` излиза веднъж като „0", веднъж като „"
        reason: 'unknown-slot-day' as const,
      })),
    };
  }

  for (const raw of входни) {
    const date = String(raw || '').trim();
    if (!ISO_DATE.test(date) || !isRealIsoDate(date)) {
      rejected.push({ date, reason: 'unreadable' });
      continue;
    }
    // Дубликатът се мери срещу ПРИЕТИТЕ, не срещу видяните. Иначе една и съща дата излиза с ДВЕ
    // противоречиви причини: първата поява „outside-year", втората „duplicate" — и екранът
    // показва два различни отговора за един вход.
    if (seen.has(date)) {
      rejected.push({ date, reason: 'duplicate' });
      continue;
    }
    // Извън учебната година денят не връща курса вътре — той го мести НАВЪН по друг път.
    if (opts.frame && (date < opts.frame.startDate || date > opts.frame.endDate)) {
      rejected.push({ date, reason: 'outside-year' });
      continue;
    }
    // Ден, различен от деня на слота, НЕ създава занятие — само заместващото занятие (R0б) може
    // това. Мълчаливото приемане би записало стойност без ефект и админът би чакал резултат,
    // който няма да дойде.
    const d = new Date(`${date}T00:00:00.000Z`);
    const isoDow = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
    if (isoDow !== dow) {
      rejected.push({ date, reason: 'wrong-weekday' });
      continue;
    }
    // Таванът се прилага ТУК, а не се обявява само в константа: обявена ограда, която нищо не
    // налага, е обещание без покритие — класът, който тази верига затваря от седмица.
    if (accepted.length >= MAX_DECLARED_SCHOOL_DAYS) {
      rejected.push({ date, reason: 'too-many' });
      continue;
    }
    seen.add(date);
    accepted.push(date);
  }

  accepted.sort();
  return { accepted, rejected };
}

/**
 * Първите `lessonCount` дати ≥ `startDate`, които са в деня от седмицата на
 * слота и НЕ попадат в неучебен диапазон — освен когато денят е ОБЯВЕН
 * (`declaredSchoolDays`), тогава ваканцията не го пропуска.
 *
 * `complete: false` (без изключение) при: `lessonCount` под 1, ден, който
 * `calendarDayToDow` не разпознава, невалидна начална дата, и ударен таван.
 * Неделята СЕ брои — виж коментара при `CALENDAR_DAY_TO_DOW` защо календарът има
 * своя карта на дните, различна от тази на заплатите. Извикващият решава какво значи това; в календарите означава
 * „върни се към разгъването": слот, чийто план не важи, не бива да ИЗЧЕЗВА.
 *
 * Инвариантът, който всички консуматори спазват: празен календар без обяснение
 * е забранен. Празен календар, в който всеки ден носи маркер за неучебен
 * период, е позволен — той е сигналът, че данните за ваканциите са счупени.
 */
export function lessonDatesForSlot(input: LessonDatesInput): LessonDates {
  const { dayOfWeek, startDate, lessonCount, nonWorking, endDate, declaredSchoolDays } = input;

  if (!Number.isFinite(lessonCount)) return incomplete();
  // Закръглянето е ТУК, преди гарда: 0 < lessonCount < 1 дава target 0, тоест
  // празен план, който по дължина би изглеждал „пълен". Такъв слот трябва да
  // падне на стария прозорец, не да изчезне от календара.
  const target = Math.floor(lessonCount);
  if (target <= 0) return incomplete();

  const dow = calendarDayToDow(dayOfWeek);
  if (!dow) return incomplete();

  const m = ISO_DATE.exec(String(startDate || ''));
  if (!m) return incomplete();
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const cursor = new Date(Date.UTC(year, month - 1, day));
  // `Date.UTC` превърта („2026-13-45" става друга дата) — сверката с входа е
  // единственият начин невалидната дата да си остане невалидна.
  if (
    Number.isNaN(cursor.getTime()) ||
    cursor.getUTCFullYear() !== year ||
    cursor.getUTCMonth() !== month - 1 ||
    cursor.getUTCDate() !== day
  ) {
    return incomplete();
  }

  // Повреден диапазон (липсваща дата) се изхвърля; обърнат (край преди начало)
  // просто не съвпада с нищо при сравнението на низове — и в двата случая
  // сметката продължава, вместо да гръмне. Същото прави `monthWorkingCalendar`.
  const ranges = (nonWorking || []).filter((r) => r && r.startDate && r.endDate);

  const stopAt = endDate && ISO_DATE.test(endDate) ? endDate : null;

  /**
   * Обявените дни се четат в множество и САМО в четим ISO вид — нечетима стойност се изхвърля,
   * вместо да свали целия план. Същата поза като при повредения диапазон отгоре: сметката
   * продължава, за да не изчезне курсът заради една сгрешена дата.
   */
  const обявени = new Set((declaredSchoolDays || []).filter((d) => ISO_DATE.test(String(d))));

  const dates: string[] = [];
  for (let scanned = 0; dates.length < target && scanned < MAX_DAYS_SCANNED; scanned++) {
    const iso = `${cursor.getUTCFullYear()}-${pad2(cursor.getUTCMonth() + 1)}-${pad2(cursor.getUTCDate())}`;
    if (stopAt && iso > stopAt) break;
    const isoDow = cursor.getUTCDay() === 0 ? 7 : cursor.getUTCDay();
    // Обявеният ден ПОБЕЖДАВА ваканцията — но само в деня от седмицата на слота. Той не създава
    // занятие в друг ден; за това служи заместващото занятие (R0б).
    const вВаканция = ranges.some((r) => iso >= r.startDate && iso <= r.endDate);
    if (isoDow === dow && (!вВаканция || обявени.has(iso))) dates.push(iso);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return {
    dates,
    lastDate: dates.length > 0 ? dates[dates.length - 1] : null,
    complete: dates.length === target,
  };
}

// ============================================================================
// Платената подготовка (TASK_46) — от началото на работната година до първото занятие
// ============================================================================

/** Защо една година няма прозорец за подготовка — всяка причина има свой текст в настройките. */
export type PreparationWindowReason =
  | 'off'
  | 'no-work-start'
  | 'not-before-start'
  | 'all-before-september'
  | 'invalid-year';

export interface PreparationWindow {
  /** Затвореният прозорец (ISO, двата края включени), или `null` — тогава `reason` казва защо. */
  window: DateWindow | null;
  reason: PreparationWindowReason | null;
  /**
   * Работната година започва преди 1 септември → дните до 31 август са ИЗРЯЗАНИ.
   *
   * Те са месеци от ПРЕДИШНАТА учебна година (`schoolYearOfMonth('2026-08')` = 2025/2026):
   * декларацията за август зарежда слотовете на старата година и не би ги видяла, тоест
   * договореното щеше да ги плати, а декларацията им — да блокира. Изрязването не е тихо:
   * екранът с настройките показва изрязания прозорец и причината (решение на гейта, 02.10).
   */
  clipped: boolean;
  /** Календарни дни в прозореца, двата края включени; 0 без прозорец. Печатна грешка в
   *  началото на работната година се вижда като число, не само като дата. */
  days: number;
}

const ДЕН_MS = 86_400_000;

/**
 * Прозорецът на платената подготовка за ЕДНА учебна година — [начало на работната година,
 * начало на занятията − 1 ден], ГЛОБАЛНО (не по началото на категорията).
 *
 * Няма прозорец (днешното поведение, без грешка), когато: отметката „Платена подготовка" е
 * изключена · началото на работната година липсва или е нечетимо · то не е ПРЕДИ началото на
 * занятията · целият прозорец пада преди 1 септември.
 *
 * Началото на занятията минава през `slotStartDate` — същия резолвер, по който тръгват и самите
 * занятия, тоест прозорецът свършва точно в деня преди първото от тях.
 */
export function preparationWindow(
  schoolYear: string | null | undefined,
  yearDates:
    | { workYearStartDate?: string; schoolYearStartDate?: string; paidPreparation?: boolean }
    | null
    | undefined,
): PreparationWindow {
  const none = (reason: PreparationWindowReason): PreparationWindow => ({
    window: null,
    reason,
    clipped: false,
    days: 0,
  });

  if (yearDates?.paidPreparation !== true) return none('off');
  const workStart = yearDates.workYearStartDate;
  if (!workStart || !isRealIsoDate(workStart)) return none('no-work-start');

  const years = yearsOf(schoolYear);
  if (!years) return none('invalid-year');

  const lessonsStart = slotStartDate({ schoolYear, yearDates });
  if (!lessonsStart || workStart >= lessonsStart) return none('not-before-start');
  const endDate = денПреди(lessonsStart);
  if (!endDate) return none('not-before-start');

  const септември = `${years[0]}-09-01`;
  const clipped = workStart < септември;
  const startDate = clipped ? септември : workStart;
  if (startDate > endDate) return { ...none('all-before-september'), clipped: true };

  const days = Math.round((Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / ДЕН_MS) + 1;
  return { window: { startDate, endDate }, reason: null, clipped, days };
}

/**
 * 🔴 ПОДГОТВИТЕЛНИТЕ ДНИ на един слот — ЕДИН израз за всички консуматори (TASK_46 §2.7):
 * очакваното на сървъра (`loadTeacherSlots` → формата получава датите готови), договореното на
 * учителя и договореното на помощника (`contracted.ts`, на таблото и в `preparePayrollYear`).
 *
 * Ден е подготвителен, когато е в прозореца (`preparationWindow`), пада в деня от седмицата на
 * слота, НЕ е в неучебен период, и е ПРЕДИ началото на занятията на САМИЯ слот (`slotStartDate`
 * с изключението на категорията). Последното е „без двойно броене": категория, тръгнала по-рано
 * (07.09), вече има занятия в прозореца и получава 0; тръгнала по-късно (02.11) получава целия
 * прозорец; тръгнала по средата (10.09) — само дните преди първото си занятие.
 *
 * ⚠️ Подготовката НЕ е занятие: не влиза в календарите, в броя занятия, в края на курса, в
 * `weeksPerYear` (§2.5). Затова живее до `lessonDatesForSlot`, а не вътре в него.
 */
export function preparationDatesForSlot(input: {
  schoolYear: string | null | undefined;
  yearDates:
    | { workYearStartDate?: string; schoolYearStartDate?: string; paidPreparation?: boolean }
    | null
    | undefined;
  /** `CategoryDefinition.startDate` — изключението „начало" на категорията. */
  categoryStartDate?: string | null;
  /** Както е записан в слота ("wednesday", "сряда"). */
  dayOfWeek: string;
  nonWorking: NonWorkingRange[];
}): string[] {
  const { window } = preparationWindow(input.schoolYear, input.yearDates);
  if (!window) return [];
  const dow = calendarDayToDow(input.dayOfWeek);
  if (!dow) return [];
  const lessonStart = slotStartDate({
    schoolYear: input.schoolYear,
    yearDates: input.yearDates,
    categoryStartDate: input.categoryStartDate ?? null,
  });
  const ranges = (input.nonWorking || []).filter((r) => r && r.startDate && r.endDate);

  const dates: string[] = [];
  const cursor = new Date(`${window.startDate}T00:00:00.000Z`);
  for (;;) {
    const iso = `${cursor.getUTCFullYear()}-${pad2(cursor.getUTCMonth() + 1)}-${pad2(cursor.getUTCDate())}`;
    if (iso > window.endDate) break;
    // От първото занятие на слота нататък денят е занятие, не подготовка.
    if (lessonStart && iso >= lessonStart) break;
    const isoDow = cursor.getUTCDay() === 0 ? 7 : cursor.getUTCDay();
    if (isoDow === dow && !ranges.some((r) => iso >= r.startDate && iso <= r.endDate)) dates.push(iso);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

/**
 * 🔴 БРОИ ЛИ СЕ този слот за плана на курса — ЕДИН израз за ЧЕТИРИТЕ места, които го питат.
 *
 * Липсващ `status` значи АКТИВЕН: полето е добавено по-късно и заварените записи го нямат, а слот
 * без него се провежда. Строгото `=== 'active'` ги изключва мълчаливо.
 *
 * ⚠️ Съществува, защото „еднакво условие на четири места" се приложи НАПОЛОВИНА и произведе
 * по-лошия дефект от този, който поправяше:
 *  · `updateScheduleSlot` изваждаше стария ден по мекото правило, а добавяше новия по строгото
 *    (`finalStatus` е `undefined` за заварен слот в заявка без `status`) — тоест при преместване
 *    сряда → събота денят се вадеше и НЕ се добавяше, категорията оставаше без слотове, и гардът
 *    ПРОПУСКАШЕ курс, който вече не се побира. Мълчание вместо фалшив отказ — по-лошата посока;
 *  · `loadFrameContext` броеше заварените, а прогнозата на екран „Категории" — не, тоест екранът
 *    показваше „побира се" за курс, чийто запис сървърът отказва.
 *
 * Затова е ИЗРАЗ, а не правило: правило се преразказва, израз се вика.
 */
export function slotCountsForPlan(status: string | null | undefined): boolean {
  return !status || status === 'active';
}

/** Един курс, както изглежда за проверката „побира ли се в учебната година" (C4 R3/R9). */
export interface CategoryFrameInput {
  categoryId: string;
  categoryName: string;
  /** РЕШЕНОТО начало на занятията (`slotStartDate`), не суровото поле на категорията. */
  startDate: string;
  /** Броят занятия — `CategoryDefinition.weeksPerYear`. */
  lessonCount: number;
  /**
   * Дните от седмицата на РЕАЛНИТЕ слотове на категорията.
   *
   * Празен списък значи „няма слотове" и курсът НЕ се брои за излязъл: категория без слотове
   * е нормално състояние в началото на годината и няма занятия, които да паднат отвън.
   */
  dayOfWeeks: string[];
}

/** Курс, чието последно занятие пада след края на учебната година. */
export interface CategoryOverflow {
  categoryId: string;
  categoryName: string;
  /** Последното занятие на курса (ISO) — датата, която съобщението показва. */
  lastDate: string;
  /** Колко занятия падат СЛЕД края на годината. Винаги ≥ 1 в този списък. */
  overflowCount: number;
}

/**
 * 🔴 ЕДИН израз за „кои неучебни периоди влизат в СМЕТКАТА" — за сървъра и за екрана.
 *
 * Двете страни хранеха `categoryLessonForecast` с РАЗЛИЧНИ ваканции и разликата беше невидима,
 * докато и двете режеха плана на един и същ календарен ръб. След като ръбът отпадна, тя излезе
 * наяве:
 *  · ваканция с начало без край — сървърът я броеше за ЕДНОДНЕВНА, екранът я ИЗХВЪРЛЯШЕ;
 *  · заварена ваканция извън прозореца — екранът я КЛИПВАШЕ, сървърът я броеше цяла.
 *
 * Резултатът е противоречието, което цялата тази верига съществува да направи невъзможно:
 * подсказката казва „побира се", а `updateCategory` отказва записа. Затова тук стои ПРАВИЛОТО,
 * а двете страни само го викат — правило се преразказва, израз се вика.
 *
 * Изборът при липсващ край е СЪРВЪРНИЯТ (еднодневна), не клиентският (изхвърляне): гардът греши
 * безопасно, когато преувеличава неучебното време, и опасно, когато го подценява — подценяването
 * е точно посоката, в която той мълчи.
 *
 * ⚠️ `holidaySourcesFrom` (КАЛЕНДАРИТЕ) остава с другото правило и това е съзнателно: там
 * измислената граница би потиснала часове, които реално се провеждат. Асиметрията е между
 * ПОКАЗВАНЕ и РЕШАВАНЕ, не между два екрана.
 *
 * ⚠️ Тук НЕ се клипва към прозорец. Заварена ваканция `2020-01-01 … 2077-12-31` покрива целия
 * хоризонт и оставя курса без нито едно занятие — но това вече не минава за „побира се":
 * `categoriesOutsideSchoolYear` го праща в `unverified`. Клипване тук би СКРИЛО повредения запис
 * вместо да го покаже.
 */
export function nonWorkingForFit(
  holidays: Array<{ startDate?: unknown; endDate?: unknown } | null | undefined> | null | undefined,
): NonWorkingRange[] {
  const out: NonWorkingRange[] = [];
  for (const h of holidays || []) {
    const startDate = holidayDateToIso(h?.startDate);
    if (!startDate) continue; // без начало периодът не съществува; повреден запис не къса записа
    const end = holidayDateToIso(h?.endDate);
    out.push({ startDate, endDate: end && end >= startDate ? end : startDate });
  }
  return out;
}

/**
 * Повреден ли е записът за ваканция — ТРИТЕ форми, едно определение.
 *
 * Корумпиран е този, който `createHoliday` не би приела: липсващо начало, липсващ край, или край
 * ПРЕДИ началото. `nonWorkingForFit` обработва и трите мълчаливо (изхвърля, брои за еднодневна,
 * свива до началото), защото гардът трябва да остане работещ — но обработеното трябва да е и
 * ВИДИМО, а видимостта иска едно определение, не второ копие в извикващия.
 *
 * 🔴 Стои ТУК, до правилото, което ги обработва. Първата версия на доклада живееше в сървъра и
 * назоваваше само СРЕДНАТА форма; изхвърленият запис ПОДЦЕНЯВА неучебното време, тоест е точно
 * посоката, в която гардът мълчи.
 */
export function isCorruptHolidayRecord(h: { startDate?: unknown; endDate?: unknown } | null | undefined): boolean {
  if (!h) return false; // няма запис — няма какво да е повредено
  const start = holidayDateToIso(h.startDate);
  const end = holidayDateToIso(h.endDate);
  /**
   * 🔴 ДВЕТЕ нечетими дати са НАЙ-повреденият запис, не „празен ред".
   *
   * Първата версия връщаше тук `false` с обосновка „празен ред". Тя щеше да важи за суров ред от
   * Excel — но единственият консуматор чете ДОКУМЕНТИ ОТ FIRESTORE, а там празен запис не
   * съществува: `createHoliday` иска и двете дати. Тоест документ с две нечетими стойности (дата,
   * писана пряко като „23.12.2026") се изхвърляше от `nonWorkingForFit` И не се докладваше —
   * гардът подценява неучебното време, точно посоката, в която той мълчи.
   *
   * ⚠️ Подаде ли някой суровите редове на Excel насам, празните редове се филтрират ПРЕДИ това.
   */
  if (!start || !end) return true;
  return end < start;
}

/**
 * Суровата дата на ваканция → ISO „YYYY-MM-DD" по UTC; `null` при липсваща или неразпозната.
 *
 * Формите са ЧЕТИРИ според пътя: Firestore `Timestamp` (`.toDate()`), `{_seconds}` (сериализация
 * на callable), `{seconds}` (клиентският SDK) и `Date`. Сървърът познаваше само първата, клиентът
 * — първите три. Един парсер, за да не покрива всяка страна различно подмножество.
 *
 * UTC, а не локално: `holidayManagement.parseDate` записва `Date.UTC(y, m-1, d)`, тоест UTC
 * полунощ, и локално четене западно от Гринуич връща ПРЕДИШНИЯ ден.
 */
export function holidayDateToIso(ts: unknown): string | null {
  if (!ts) return null;

  if (typeof ts === 'string') {
    const iso = ts.slice(0, 10);
    return isRealIsoDate(iso) ? iso : null;
  }

  let d: Date | null = null;
  if (ts instanceof Date) {
    d = ts;
  } else if (typeof ts === 'object') {
    const o = ts as Record<string, unknown>;
    if (typeof o.toDate === 'function') {
      d = (o.toDate as () => Date)();
    } else {
      const secs =
        typeof o._seconds === 'number' ? o._seconds : typeof o.seconds === 'number' ? o.seconds : null;
      if (secs !== null) d = new Date(secs * 1000);
    }
  }
  if (!d || Number.isNaN(d.getTime())) return null;

  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}-${m}-${day}`;
}

/** Резултатът от проверката „кои курсове не се побират в учебната година". */
export interface SchoolYearFitResult {
  /** Курсове с ДОКАЗАНО занятие след края на годината. */
  outside: CategoryOverflow[];
  /**
   * `categoryId` на курсове, за които „побира се" НЕ може да бъде твърдяно: планът е непълен, а
   * сред намерените занятия няма нито едно след края.
   *
   * 🔴 Измерено НЕДОСТИЖИМО и остава като условие, не като екран: 11 760 производими
   * конфигурации (рамка × ваканции × начало × седмици × ден), 42 непълни плана, **0** от тях с
   * нулев overflow. Механизмът е структурен — ваканция не може да излезе извън `holidayWindow`,
   * а прагът е ВЪТРЕ в него, тоест щом ваканциите свършат, занятията вървят седмично и всички
   * падат след прага. Непълен план значи „изтласкан далеч напред", а изтласкан напред значи
   * „много занятия отвън".
   *
   * Съществува, защото инвариантът трябва да е налаган от кода, а не от аргумент: ако бъдеща
   * промяна направи ъгъла достижим, тук ще има запис вместо мълчаливо „побира се".
   */
  unverified: string[];
}

/**
 * Кои курсове излизат извън рамката на учебната година (C4 R3 и R9).
 *
 * 🔴 ЕДИН източник за ТРИМА консуматора: блокирането при запис на категория, блокирането при
 * запис на слот, и списъкът, който `createHoliday`/`updateHoliday` връщат след успешен запис.
 * Второ копие би се разминало при първата промяна — точно както се разминаха двата
 * нормализатора на годината и двете разгъвания на слота.
 *
 * Асиметрията между консуматорите е в ТЯХ, не тук: категорията и слотът са план и могат да
 * бъдат отказани, ваканцията е факт от живота и се записва винаги. Тази функция само брои.
 *
 * ⚠️ Смята се ПО КАТЕГОРИЯ с дедуп по ден от седмицата (`categoryLessonForecast`), защото
 * въпросът е „кога свършва курсът" — два съботни слота не го местят по-нататък. НЕ ползвай
 * този резултат за очаквани ЧАСОВЕ: там всеки слот е отделно задължение и се брои поотделно.
 *
 * ═══ ЗАЩО ТУК НЯМА ГРАНИЦА НА ТЪРСЕНЕТО ═══
 *
 * 🔴 Имаше, и се счупи ТРИ пъти подред — всеки път поправена точково и всеки път изплувала по
 * друг път: `"-12-31"` при нечетим ключ (сортира под всяка ISO дата), после `frame.endDate`
 * (равна на прага), после `holidayWindow.endDate` (МОЖЕ да е равна на прага). Общото:
 * `lessonDatesForSlot` спира ВКЛЮЧИТЕЛНО на подадения край, тоест граница, която не НАДХВЪРЛЯ
 * прага, прави проверката структурно сляпа — нула намерени занятия след края, винаги.
 *
 * Мярката, която затвори въпроса: при край на годината `2027-08-30` границата НАДХВЪРЛЯШЕ прага
 * (`2027-08-31`) и проверката пак беше сляпа, а при `2027-09-30` границата НЕ го надхвърляше и
 * мълчанието беше ВЯРНО. Тоест „границата да е след прага" не е нито необходимо, нито
 * достатъчно условие; истинското свойство е ПЪЛНОТАТА на плана.
 *
 * Затова полето не е поправено, а ПРЕМАХНАТО: няма граница, която да бъде сгрешена четвърти
 * път. Хоризонтът е собственият таван на скенера (`MAX_DAYS_SCANNED`) — той вече пази от избягал
 * план и е причината `LessonDatesInput.endDate` изобщо да съществува. Календарен ръб не добавяше
 * защита; само режеше истината. Мащабът на отрязаното при приемани стойности: начало = краят на
 * годината `2027-12-31` с 52 седмици дава последно занятие `2028-12-23` — над ГОДИНА след прага,
 * а проверката виждаше нула.
 *
 * ⚠️ Съзнателна граница: отвъд прозореца за ваканции няма записани неучебни дни, тоест сметката
 * приема всяка седмица за учебна. Това мести изчисленото последно занятие по-РАНО от реалността
 * — може да подцени броя, но не може да произведе фалшив отказ.
 */
export function categoriesOutsideSchoolYear(input: {
  categories: CategoryFrameInput[];
  /** Краят на учебната година — прагът. */
  schoolYearEnd: string;
  nonWorking: NonWorkingRange[];
}): SchoolYearFitResult {
  const { categories, schoolYearEnd, nonWorking } = input;
  const outside: CategoryOverflow[] = [];
  const unverified: string[] = [];

  for (const category of categories) {
    const forecast = categoryLessonForecast({
      dayOfWeeks: category.dayOfWeeks,
      startDate: category.startDate,
      lessonCount: category.lessonCount,
      nonWorking,
      workYearEnd: schoolYearEnd,
    });

    // Без слотове няма занятия, тоест няма какво да излезе — единственото ДОКАЗУЕМО „побира се"
    // без сметка. Категория без часове е нормално състояние в началото на годината.
    if (!forecast.hasSlots) continue;

    /**
     * 🔴 ЕДИНСТВЕНАТА врата, през която курс излиза като „побира се" — и тя иска ПЪЛЕН план.
     *
     * Редът на трите проверки Е самото свойство, не последователност. Предишната версия
     * пропускаше `lastDate === null` през първия `continue`, тоест „не мога да сметна" излизаше
     * през вратата на „побира се" и не стигаше до `unverified` — гардът, който точно това
     * състояние трябваше да улови, мълчеше за него.
     *
     * Измерено, че пътят е реален: заварена ваканция `2020-01-01 … 2077-12-31` (стойност с
     * измерване в докблока на `holidayWindow`) не оставя нито едно занятие на НИТО ЕДНА
     * категория — тоест един повреден запис обявяваше цялата година за побираща се.
     */
    if (forecast.complete && forecast.overflowCount <= 0) continue;

    /**
     * ⚠️ Тази врата приема и НЕПЪЛЕН план — нарочно, но с цена, която трябва да е казана.
     *
     * Планът е непълен, когато разгъвачът не е събрал всички занятия в 800-те си дни. Тогава
     * `overflowCount` и `lastDate` са ДОЛНИ граници: занятия отвън има поне толкова и последното
     * е поне тогава. Изхвърлянето им би било по-лошо — курс изчерпва разгъвача именно защото
     * излиза далеч след края, тоест мълчанието би застигало курсовете, за които предупреждението
     * е най-нужно.
     *
     * 🔴 Следствие, което живее ОТВЪД тази функция: екранът „Категории" гейтва текста си по
     * `LessonForecast.complete` и при непълен план казва „ПОНЕ N занятия" без дата, докато
     * сървърният отказ носи датата като точна. Тоест двата текста се разминават по увереност,
     * не по число. Изнасянето на границата в самия отказ е отделна работа; дотогава разликата е
     * тук, назована, а не открита при следващото ревю.
     */
    if (forecast.overflowCount > 0 && forecast.lastDate) {
      outside.push({
        categoryId: category.categoryId,
        categoryName: category.categoryName,
        lastDate: forecast.lastDate,
        overflowCount: forecast.overflowCount,
      });
      continue;
    }

    // Останалото не е нито доказано побиращо се, нито изброимо: непълен план, липсваща последна
    // дата, нечетим брой седмици. Признание, не твърдение.
    unverified.push(category.categoryId);
  }

  return { outside, unverified };
}

export interface LessonForecastInput {
  /** Дните от седмицата на РЕАЛНИТЕ слотове на категорията ("saturday", "събота"). */
  dayOfWeeks: string[];
  /** Решеното начало на занятията (ISO) — `slotStartDate`. */
  startDate: string;
  /** Брой занятия (`CategoryDefinition.weeksPerYear`). */
  lessonCount: number;
  /** Неучебните периоди на годината. */
  nonWorking: NonWorkingRange[];
  /** Прагът „не се побира" — краят на работната година (`workYearEndDate`). */
  workYearEnd: string | null;
  /**
   * Таван на търсенето (ISO).
   *
   * 🔴 НУЛА производствени консуматора и това е нарочно — не го добавяй на нов път.
   *
   * Тук стоеше „без него курс с много ваканции може да бяга напред", тоест ПРЕПОРЪКА. Точно
   * тази препоръка беше механизмът, който ослепи гарда три пъти: план, отрязан на календарен
   * ръб, връща „нула занятия след края" за курс, който излиза с година. Бягането напред се
   * спира от `MAX_DAYS_SCANNED`, не оттук.
   *
   * Параметърът оцелява САМО като лост на вградената калибрация: тестът връща стария таван и
   * доказва, че мълчанието се появява точно там, където беше измерено. Без него мутацията
   * става разказ и умира при първия рефакторинг. Жив параметър, документиран като
   * препоръчителен, е начинът същата слепота да се върне четвърти път.
   */
  windowEnd?: string;
}

export interface LessonForecast {
  /** Най-късното последно занятие сред дните на категорията; `null` при празен резултат. */
  lastDate: string | null;
  /** Колко занятия падат СЛЕД прага. 0 = всичко се побира. */
  overflowCount: number;
  /** Имаше ли изобщо слотове, от които да се смята. */
  hasSlots: boolean;
  /** Планът пълен ли е за ВСеки ден — непълен план не носи истина за „последно занятие". */
  complete: boolean;
}

/**
 * Докъде стига курсът и колко занятия остават след края на работната година (R5).
 *
 * Смята се от РЕАЛНИТЕ слотове на категорията: броят седмици е на категорията, но дните от
 * седмицата са на слотовете, и категория с два дни свършва по-рано от същата с един. При
 * повече от един ден се взима най-КЪСНОТО последно занятие — то определя кога приключва
 * курсът.
 *
 * 🔴 Нищо не се реже и не се крие (решение 8 „fail-visible"): предупреждението носи числа,
 * а календарите продължават да показват всички занятия. Календар, който тихо изрязва
 * занятията след 30 юни, изглежда точно като календар на курс, който свършва навреме —
 * и админът няма как да разбере, че трябва да разшири работната година или да намали
 * седмиците.
 *
 * Празен списък със слотове НЕ е грешка: категория без слотове е нормално състояние в
 * началото на годината (`hasSlots: false`, без изключение).
 */
export function categoryLessonForecast(input: LessonForecastInput): LessonForecast {
  const { dayOfWeeks, startDate, lessonCount, nonWorking, workYearEnd, windowEnd } = input;

  // 🔴 Дедупът е по ДЕН ОТ СЕДМИЦАТА, не по низа: базата пази и „saturday", и „събота" за
  // един и същ ден (същият клас разминаване, който `foldConfusables` лекува при имената на
  // класове), и дедуп по низ би броил един курс два пъти — тоест би обявил двойно повече
  // занятия след края, отколкото има.
  const byDow = new Map<number, string>();
  for (const d of dayOfWeeks || []) {
    const dow = calendarDayToDow(d);
    if (dow !== undefined && !byDow.has(dow)) byDow.set(dow, d);
  }
  if (byDow.size === 0) {
    return { lastDate: null, overflowCount: 0, hasSlots: false, complete: false };
  }

  let lastDate: string | null = null;
  let overflowCount = 0;
  let complete = true;

  // Всеки различен ден се смята веднъж: два слота в събота дават един и същ план, а
  // събирането на техните занятия би удвоило K без нито едно допълнително занятие.
  for (const day of byDow.values()) {
    const plan = lessonDatesForSlot({
      dayOfWeek: day,
      startDate,
      lessonCount,
      nonWorking,
      endDate: windowEnd,
    });
    if (!plan.complete) complete = false;
    if (plan.lastDate && (lastDate === null || plan.lastDate > lastDate)) {
      lastDate = plan.lastDate;
    }
    if (workYearEnd) {
      overflowCount += plan.dates.filter((d) => d > workYearEnd).length;
    }
  }

  return { lastDate, overflowCount, hasSlots: true, complete };
}
